import React, { useEffect, useState, useRef } from "react";
import styled, { css } from "styled-components";
import { Box, Button } from "rebass/styled-components";
import { FaChevronUp, FaChevronDown, FaSearch } from "react-icons/fa";

import theme from "theme/themeThree";
import { CheckboxWithLabel } from "MVVM/Components/Checkbox";
import { Divider } from "MVVM/Components/Divider";

export interface IOption {
  value: string;
  label: string;
  description: string;
}

interface ICustomMultiSelectProps {
  options: IOption[];
  placeholder?: string;
  isSearchable?: boolean;
  disabled?: boolean;
  onChange: (option: IOption) => void;
  value: IOption[];
}

export const CustomMultiSelect = ({
  options,
  placeholder = "Select options",
  isSearchable = false,
  disabled = false,
  onChange,
  value,
}: ICustomMultiSelectProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const [selectedOptions, setSelectedOptions] = useState<IOption[]>(
    value || [],
  );
  const [searchTerm, setSearchTerm] = useState("");
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    setSelectedOptions(value);
  }, [value]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === "Escape") setIsOpen(false);
  };

  const filteredOptions = isSearchable
    ? options.filter((option) =>
        option.description.toLowerCase().includes(searchTerm.toLowerCase()),
      )
    : options;

  const handleSelectOption = (option: IOption) => {
    let newSelected: IOption[];

    if (selectedOptions.some((selected) => selected.value === option.value)) {
      newSelected = selectedOptions.filter((o) => o.value !== option.value);
    } else {
      newSelected = [...selectedOptions, option];
    }

    setSelectedOptions(newSelected);
    onChange(option);
  };

  return (
    <StyledMultiSelectContainer ref={dropdownRef}>
      <StyledSelectBox
        onClick={() => !disabled && setIsOpen(!isOpen)}
        $isOpen={isOpen}
        $disabled={disabled}
      >
        {selectedOptions.length === 0 ? (
          <>
            <StyledText as="span">{placeholder}</StyledText>
            {isOpen ? (
              <FaChevronUp fill={theme.colors.chalk} />
            ) : (
              <FaChevronDown fill={theme.colors.chalk} />
            )}
          </>
        ) : (
          <StyledText as="span">
            {selectedOptions[0].description}
            {selectedOptions.length > 1 && (
              <StyledText as="span" style={{ marginLeft: "5px" }}>
                + {selectedOptions.length - 1} more
              </StyledText>
            )}
            <SelectedOptionButton
              onClick={() => {
                onChange({ value: "", label: "", description: "" });
                setSelectedOptions([]);
              }}
            >
              &times;
            </SelectedOptionButton>
          </StyledText>
        )}
      </StyledSelectBox>

      {isOpen && (
        <StyledDropdown>
          {isSearchable && (
            <StyledSearchContainer>
              <StyledSearchIcon />
              <StyledSearchInput
                as="input"
                type="text"
                placeholder={`Search ${placeholder}...`}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                onFocus={() => setIsOpen(true)}
                onKeyDown={handleKeyDown}
              />
            </StyledSearchContainer>
          )}
          <div>
            {selectedOptions.length > 0 &&
              selectedOptions.map((option, index) => (
                <>
                  <SelectedOptionsSection
                    key={option.value}
                    onClick={() => handleSelectOption(option)}
                  >
                    <CheckboxWithLabel
                      checkbox={{
                        checked: true,
                      }}
                      readonly
                    >
                      {option.description}
                    </CheckboxWithLabel>
                  </SelectedOptionsSection>
                  {index === selectedOptions.length - 1 && (
                    <Divider
                      width="100%"
                      mt={0}
                      mb={0}
                      color="chalkAlpha20"
                    ></Divider>
                  )}
                </>
              ))}
            {filteredOptions.length > 0 ? (
              [...filteredOptions]
                .sort((a, b): any => {
                  const numA = Number(a.description);
                  const numB = Number(b.description);

                  if (!isNaN(numA) && !isNaN(numB)) return numA - numB;
                  return a.description.localeCompare(b.description, undefined, {
                    numeric: true,
                  });
                })
                .map((option) => (
                  <StyledOptions
                    key={option.value}
                    onClick={() => handleSelectOption(option)}
                  >
                    <CheckboxWithLabel
                      checkbox={{
                        checked: selectedOptions.some(
                          (selected) => selected.value === option.value,
                        ),
                      }}
                    >
                      {option.description}
                    </CheckboxWithLabel>
                  </StyledOptions>
                ))
            ) : (
              <NoResultsContainer>
                <StyledText as="span">No results found</StyledText>
                <NoResultsCopy>Try searching another keyword</NoResultsCopy>
              </NoResultsContainer>
            )}
          </div>
        </StyledDropdown>
      )}
    </StyledMultiSelectContainer>
  );
};

const hoverStyles = css`
  border: 2px solid ${({ theme }) => theme.colors.darkerEnvy};
  background-color: ${({ theme }) => theme.colors.envyAlpha05};
`;

const StyledSelectBox = styled(Box)<{ $isOpen: boolean; $disabled: boolean }>`
  align-items: center;
  background-color: ${({ theme }) => theme.colors.navy};
  border: 2px solid transparent;
  border-radius: 100px;
  box-sizing: border-box;
  color: ${({ theme }) => theme.colors.ice};
  cursor: ${({ $disabled }) => ($disabled ? "not-allowed" : "pointer")};
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  height: 46px;
  justify-content: space-between;
  min-height: 38px;
  min-width: 100%;
  outline: 0 !important;
  padding: 10px 16px;
  position: relative;
  transition: all 100ms;
  width: max-content;
  white-space: nowrap;

  &:hover {
    ${({ $disabled }) => !$disabled && hoverStyles}
  }

  ${({ $isOpen, $disabled }) => $isOpen && !$disabled && hoverStyles}
`;

const StyledDropdown = styled(Box)`
  background-color: ${({ theme }) => theme.colors.ned};
  border: ${({ theme }) => `1px solid ${theme.colors.chalkAlpha20}`};
  border-radius: 16px;
  box-shadow: 0px 4px 4px 0px rgba(0, 0, 0, 0.25);
  color: ${({ theme }) => theme.colors.ice};
  cursor: pointer;
  height: fit-content;
  left: 0;
  max-height: 380px;
  overflow: hidden;
  overflow-y: auto;
  position: absolute;
  top: 100%;
  width: max-content;
  white-space: nowrap;
  z-index: 10;

  &::-webkit-scrollbar {
    width: 12px; /* width of the entire scrollbar */
  }

  &::-webkit-scrollbar-track {
    background: #192832;
    border-left: 1px solid #20323f;
  }

  &::-webkit-scrollbar-thumb {
    background-color: #253a49;
    border-radius: 4px;
    cursor: pointer;
  }
`;

const SelectedOptionsSection = styled(Box)`
  border-radius: 8px;
  cursor: pointer;
  margin: 10px;
  padding: 10px 40px 10px 10px;
`;

const StyledText = styled(Box)`
  color: ${({ theme }) => theme.colors.ice};
  font-weight: 500;
  font-size: 14px;
  line-height: 16px;
`;

const NoResultsContainer = styled(Box)`
  align-items: center;
  display: flex;
  flex-direction: column;
  height: 100px;
  justify-content: center;
`;

const NoResultsCopy = styled(Box)`
  color: ${({ theme }) => theme.colors.chalk};
  font-size: 12px;
  font-weight: 500;
  line-height: 16px;
  margin-top: 5px;
`;

const SelectedOptionButton = styled(Button)`
  background-color: ${({ theme }) => theme.colors.envy};
  border: none;
  border-radius: 100px;
  color: black;
  cursor: pointer;
  margin-left: 5px;
  padding: 0px 4px;
`;

const StyledMultiSelectContainer = styled(Box)`
  position: relative;
`;

const StyledOptions = styled(Box)`
  border-radius: 8px;
  cursor: pointer;
  margin: 10px;
  padding: 10px 40px 10px 10px;
`;

const StyledSearchIcon = styled(FaSearch)`
  fill: ${({ theme }) => theme.colors.chalk};
`;

const StyledSearchInput = styled(Box)`
  background: inherit;
  border: none;
  border-radius: 4px;
  color: ${({ theme }) => theme.colors.ice};
  font-family: Barlow, sans-serif;
  font-weight: 500;
  font-size: 14px;
  outline: none;
  padding: 5px;
  width: 100%;

  &::placeholder {
    color: ${({ theme }) => theme.colors.chalkAlpha60};
    fontweight: 500;
  }
`;

const StyledSearchContainer = styled(Box)`
  align-items: center;
  border-top-left-radius: 16px;
  border-top-right-radius: 16px;
  border-bottom: ${({ theme }) => `1px solid ${theme.colors.chalkAlpha20}`};
  display: flex;
  padding: 10px;
  position: relative;
  gap: 8px;
`;
